import { Separator } from '@change/ds-core-ui/Separator';
import { Text } from '@change/ds-core-ui/Text';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import tileTexture from '#src/app/pages/petitionDetails/shared/assets/legislativeBodies/tiles.jpg';
import { SECTION_ID_MAPPING } from '#src/app/pages/petitionDetails/shared/consts';
import { useScrollToSection, useStickyActionBarHeight } from '#src/app/pages/petitionDetails/shared/hooks';
import { InfoTooltip } from '#src/app/shared/components/tooltips';

import { ScrollTrackingWaypoint } from '../../components/ScrollTrackingWaypoint';

import { DecisionMakerCard } from './components/DecisionMakerCard';
import { LegislativeBodyCard } from './components/LegislativeBodyCard';
import { ViewAll } from './components/ViewAll';
import { useDecisionMakerSection } from './hooks/useDecisionMakerSection';
import { useTracking } from './hooks/useTracking';

const ScrollTracking: React.FC<{ petitionId: string }> = ({ petitionId }) => {
	// Offsets past the sticky mobile action bar, which can visually cover this section while it is
	// still geometrically in the viewport (CHANGE-99504). The height drops to 0 while the bar is
	// parked, e.g. on a short description where this section is on screen before the bar docks.
	const stickyActionBarHeight = useStickyActionBarHeight();

	return (
		<ScrollTrackingWaypoint
			name="petition_details_decision_maker_section_view"
			trackingData={{ petition_id: petitionId }}
			bottomOffset={stickyActionBarHeight}
		/>
	);
};

export const DecisionMakerSection: React.FC = () => {
	const {
		data: { dmsAndLBs, displayViewAllButton, totalDmCount, petitionId },
	} = useDecisionMakerSection();

	const {
		data: { refs },
	} = useScrollToSection();

	const { trackTooltipClick } = useTracking();

	const { translate } = useI18n();

	if (totalDmCount === 0) return null;

	return (
		<div id={SECTION_ID_MAPPING.DECISION_MAKERS} ref={refs[SECTION_ID_MAPPING.DECISION_MAKERS]} data-qa="dm-section">
			<ScrollTracking petitionId={petitionId} />
			<div className="-mx-4 md:mx-0">
				<div className="bg-gradient-background-weak-3 relative px-6 py-10 md:rounded-lg">
					<div
						className="absolute inset-0 bg-repeat opacity-50 md:rounded-lg"
						style={{
							backgroundImage: `url(${tileTexture})`,
						}}
					/>
					<div className="relative z-1 flex flex-col gap-6">
						<div className="flex gap-2">
							<Text variant="heading" as="h2" className="text-size-heading-small md:text-size-heading-large">
								<Translate value="corgi.petition_gamma.decision_makers_v2.section_title" />
							</Text>
							<InfoTooltip
								aria-label={translate('corgi.petition_gamma.decision_makers_v2.tooltip_label')}
								data-qa="dm-section-tooltip"
								onClick={trackTooltipClick}
								className="my-auto"
							>
								<Translate value="corgi.petition_gamma.decision_makers_v2.tooltip" />
							</InfoTooltip>
						</div>
						{dmsAndLBs.map((item) => {
							const [firstDm] = item.displayedDMs;
							if (!firstDm) return null;
							return item.type === 'legislative_body' ? (
								<div key={firstDm.id}>
									<LegislativeBodyCard legislativeBody={item} />
								</div>
							) : (
								<div key={firstDm.id}>
									<DecisionMakerCard dm={firstDm} />
								</div>
							);
						})}

						{displayViewAllButton && <ViewAll totalDmCount={totalDmCount} />}
					</div>
				</div>
			</div>
			<Separator className="mt-14 mb-5 md:mt-20" />
		</div>
	);
};
